import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Avatar,
  Chip,
  Stack,
  Paper,
  Divider,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import LinkIcon from '@mui/icons-material/Link';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import PageLayout from '../../components/layout/PageLayout';
import Can from '../../components/permissions/Can';
import { useGetCompanyByIdQuery } from '../../store/api';
import { toast } from 'react-toastify';
import {
  buildCompanyRelationParams,
  resolveCompanyLogoUrl,
} from './companyApiHelpers';

const DetailItem = ({ icon, label, value }) => (
  <Grid item xs={12} sm={6}>
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
        bgcolor: 'background.default',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'primary.light',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {label}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mt: 0.5,
              fontWeight: 500,
              wordBreak: 'break-word',
            }}
          >
            {value ?? '—'}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  </Grid>
);

const ViewCompany = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data,
    isLoading: loading,
    isError,
    error,
  } = useGetCompanyByIdQuery(
    { id, params: buildCompanyRelationParams() },
    { skip: !id }
  );

  const company = data?.data ?? data ?? null;

  useEffect(() => {
    if (isError) {
      toast.error(error?.data?.message || 'Failed to load company');
      navigate('/companies');
    }
  }, [isError, error, navigate]);

  if (loading || !company) {
    return (
      <PageLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={320}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  const logoSrc = resolveCompanyLogoUrl(company.logo);

  const statusName =
    company.status?.name ??
    company.status?.label ??
    company.status_name ??
    'Unknown';

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Top Actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/companies')}
            variant="outlined"
          >
            Back
          </Button>

          <Can permission="company-edit">
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/companies/edit/${id}`)}
            >
              Edit Company
            </Button>
          </Can>
        </Box>

        {/* Main Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 5,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 4,
              background:
                'linear-gradient(135deg, rgba(25,118,210,0.08), rgba(25,118,210,0.02))',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              {/* Company Logo */}
              <Avatar
                src={logoSrc}
                variant="rounded"
                sx={{
                  width: 90,
                  height: 90,
                  borderRadius: 4,
                  bgcolor: 'white',
                  color: 'primary.main',
                  fontSize: 34,
                  fontWeight: 700,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {!logoSrc && company.name?.charAt(0)?.toUpperCase()}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  {company.name ?? 'Company'}
                </Typography>

                <Stack
                  direction="row"
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Chip
                    icon={<CheckCircleOutlineIcon />}
                    label={statusName}
                    color={
                      statusName.toLowerCase() === 'active'
                        ? 'success'
                        : 'default'
                    }
                  />

                  {(company.currency?.code ||
                    company.currency_code ||
                    company.currency?.name) && (
                    <Chip
                      icon={<CurrencyExchangeIcon />}
                      label={
                        company.currency?.code ??
                        company.currency_code ??
                        company.currency?.name
                      }
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          </Box>

          {/* Details */}
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontWeight: 600,
              }}
            >
              Company Information
            </Typography>

            <Grid container spacing={2.5}>
              <DetailItem
                icon={<BusinessIcon fontSize="small" />}
                label="Company Name"
                value={company.name}
              />

              <DetailItem
                icon={<AlternateEmailIcon fontSize="small" />}
                label="Slug"
                value={company.slug}
              />

              <DetailItem
                icon={<EmailOutlinedIcon fontSize="small" />}
                label="Email"
                value={company.email}
              />

              <DetailItem
                icon={<EmailOutlinedIcon fontSize="small" />}
                label="Email Forward"
                value={company.email_forward}
              />

              <DetailItem
                icon={<CurrencyExchangeIcon fontSize="small" />}
                label="Currency"
                value={
                  company.currency?.name ??
                  company.currency?.code ??
                  company.currency_code
                }
              />

              <DetailItem
                icon={<LocationOnOutlinedIcon fontSize="small" />}
                label="Address"
                value={company.address}
              />

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: 'primary.light',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <LinkIcon fontSize="small" />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        Invoice URL
                      </Typography>

                      <Divider sx={{ my: 1.5 }} />

                      <Typography
                        variant="body1"
                        sx={{
                          wordBreak: 'break-word',
                          lineHeight: 1.8,
                        }}
                      >
                        {company.invoice_url || '—'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewCompany;